import React, { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { registrarLiquidacion, getPendientesProveedor, getCuentasBanco } from '../../services/data';
import { toMoney } from '../../utils/numbers';
import { dateTimeLocalGT, parseDateTimeLocalGT } from '../../utils/dates';
import ProveedorAutocomplete from '../registrar-cierre/ProveedorAutocomplete';
import MoneyInput from '../common/MoneyInput';
import '../registrar-cierre/styles/cuadre-turno.css';
import '../finanzas/ui.css';
import FondoModal from '../common/FondoModal';

const DESTINOS = [['deposito', 'Depósito bancario'], ['duena', 'Entrega de efectivo'], ['proveedor', 'Pago a proveedor'], ['otro', 'Otro pago autorizado'], ['reserva', 'Reserva para próxima apertura']];
const TIPOS_OTRO = ['Transporte', 'Servicios', 'Insumos', 'Mantenimiento', 'Sueldos y adelantos', 'Otro'];
const FORMAS = ['Efectivo', 'Transferencia', 'Cheque'];
const n = (v) => Number(v) || 0;
const ahoraLocal = dateTimeLocalGT;

// Registrar qué pasó con (parte del) efectivo de un cierre. Solo administración.
export default function RegistrarDestinoModal({ cierre, pendiente, onClose, onSaved }) {
  const [tipo, setTipo] = useState('deposito');
  const [f, setF] = useState({ monto: '', fechaAt: ahoraLocal(), banco: '', referencia: '', recibio: '', beneficiario: '', tipoPago: 'Otro',
    descripcion: '', formaPago: 'Efectivo', observacion: '', cuentaBancariaId: '' });
  const [cuentas, setCuentas] = useState([]); // cuentas de Bancos de la sucursal del cierre
  useEffect(() => { getCuentasBanco({ sucursalId: cierre.sucursalId, activas: '1' }).then(setCuentas).catch(() => setCuentas([])); }, [cierre.sucursalId]);
  const [proveedor, setProveedor] = useState({ id: null, nombre: '' });
  const [facturas, setFacturas] = useState([]);
  const [aplicar, setAplicar] = useState({});
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  useEffect(() => {
    setAplicar({}); setFacturas([]);
    if (proveedor.id) getPendientesProveedor(proveedor.id, cierre.sucursalId).then(setFacturas).catch(() => setFacturas([]));
  }, [proveedor.id, cierre.sucursalId]);

  const monto = tipo === 'proveedor' ? Object.values(aplicar).reduce((s, v) => s + n(v), 0) : n(f.monto);
  const excede = monto > pendiente + 0.001;

  const guardar = async (e) => {
    e.preventDefault();
    if (!monto) return Swal.fire('Destino del dinero', tipo === 'proveedor' ? 'Indica cuánto se paga de al menos una factura.' : 'Ingresa el monto.', 'warning');
    if (excede) return Swal.fire('Destino del dinero', 'El monto supera el efectivo pendiente de liquidar.', 'warning');
    if (tipo === 'proveedor' && !proveedor.id) return Swal.fire('Destino del dinero', 'Selecciona el proveedor.', 'warning');
    if (tipo === 'otro' && !f.beneficiario.trim()) return Swal.fire('Destino del dinero', 'Indica el beneficiario.', 'warning');
    if (tipo === 'deposito' && !f.cuentaBancariaId) return Swal.fire('Destino del dinero', 'Selecciona la cuenta bancaria. Si no existe, regístrala en Bancos.', 'warning');
    const fechaAt = parseDateTimeLocalGT(f.fechaAt);
    if (!fechaAt) return Swal.fire('Destino del dinero', 'Fecha y hora inválidas.', 'warning');
    setBusy(true);
    try {
      await registrarLiquidacion(cierre.id, {
        tipo, monto: monto.toFixed(2), fechaAt, banco: f.banco, referencia: f.referencia, recibio: f.recibio,
        beneficiario: f.beneficiario, tipoPago: f.tipoPago, descripcion: f.descripcion, formaPago: f.formaPago, observacion: f.observacion,
        cuentaBancariaId: tipo === 'deposito' ? f.cuentaBancariaId : null,
        proveedorId: proveedor.id, aplicaciones: Object.entries(aplicar).filter(([, v]) => n(v) > 0).map(([cuentaId, v]) => ({ cuentaId, monto: v })),
      });
      onSaved?.();
    } catch (err) { Swal.fire('No se pudo registrar', err.message || 'Intenta de nuevo.', 'error'); }
    finally { setBusy(false); }
  };

  return (
    <FondoModal onClose={onClose} style={{ zIndex: 1300 }}>
      <form className="fx-modal fx-modal--sm" role="dialog" aria-modal="true" aria-label="Registrar destino del dinero" onSubmit={guardar} onMouseDown={(e) => e.stopPropagation()} style={{ width: 'min(620px,100%)' }}>
        <header><div><h2>Registrar destino del dinero</h2><p>Pendiente de liquidar: <b>{toMoney(pendiente)}</b></p></div><button type="button" aria-label="Cerrar" onClick={onClose}>×</button></header>
        <div className="fx-form">
          <label className="full">Destino<select value={tipo} onChange={(e) => setTipo(e.target.value)}>{DESTINOS.map(([id, l]) => <option key={id} value={id}>{l}</option>)}</select></label>

          {tipo !== 'proveedor' && <label>Monto<MoneyInput value={f.monto} onChange={(v) => setF({ ...f, monto: v })} required autoFocus /></label>}
          <label>{tipo === 'deposito' ? 'Fecha del depósito' : 'Fecha y hora'}<input type="datetime-local" value={f.fechaAt} max={ahoraLocal()} onChange={set('fechaAt')} required /></label>

          {tipo === 'deposito' && <>
            <label>Cuenta bancaria<select value={f.cuentaBancariaId} onChange={set('cuentaBancariaId')} required>
              <option value="">— Selecciona —</option>{cuentas.map((c) => <option key={c.id} value={c.id}>{c.banco} · {c.numero}{c.nombre ? ` · ${c.nombre}` : ''}</option>)}</select>
              {!cuentas.length && <small>No hay cuentas de esta sucursal. Regístrala en Bancos.</small>}</label>
            <label>No. de boleta / referencia<input value={f.referencia} onChange={set('referencia')} maxLength={80} /></label>
          </>}
          {tipo === 'duena' && <label>Nombre de la persona que recibió<input value={f.recibio} onChange={set('recibio')} maxLength={120} required placeholder="Ej.: dueña, dueño, hija u otra persona autorizada" /></label>}
          {tipo === 'otro' && <>
            <label>Beneficiario<input value={f.beneficiario} onChange={set('beneficiario')} maxLength={120} required /></label>
            <label>Tipo<select value={f.tipoPago} onChange={set('tipoPago')}>{TIPOS_OTRO.map((t) => <option key={t}>{t}</option>)}</select></label>
            <label className="full">Descripción<input value={f.descripcion} onChange={set('descripcion')} maxLength={300} /></label>
          </>}
          {(tipo === 'otro' || tipo === 'proveedor') && <>
            <label>Forma de pago<select value={f.formaPago} onChange={set('formaPago')}>{FORMAS.map((x) => <option key={x}>{x}</option>)}</select></label>
            <label>Referencia<input value={f.referencia} onChange={set('referencia')} maxLength={80} /></label>
          </>}
          {tipo === 'proveedor' && <label className="full">Proveedor<ProveedorAutocomplete value={proveedor} soloCatalogo onChange={setProveedor} /></label>}
          <label className="full">Observación<input value={f.observacion} onChange={set('observacion')} maxLength={500} placeholder={tipo === 'reserva' ? 'Efectivo que permanece en la sucursal (no es gasto)' : ''} /></label>
          {/* Los comprobantes respaldan una instrucción: sin instrucciones no se adjuntan fotos sueltas. */}
          <small className="full fx-empty" style={{ padding: 0 }}>Para adjuntar comprobantes, primero genera las instrucciones para indicar qué debe hacerse con el efectivo.</small>
        </div>

        {tipo === 'proveedor' && proveedor.id && (facturas.length ? (
          <table className="ct-tabla">
            <thead><tr><th>Factura</th><th className="num">Saldo anterior</th><th className="num">Pago aplicado</th><th className="num">Saldo nuevo</th></tr></thead>
            <tbody>{facturas.map((d) => {
              const pago = n(aplicar[d.id]);
              return <tr key={d.id} className={pago > d.saldo ? 'ct-error' : ''}><td data-label="Factura">{d.documento || 'Sin ref.'}</td>
                <td data-label="Saldo anterior" className="num">{toMoney(d.saldo)}</td>
                <td data-label="Pago aplicado" className="num"><MoneyInput className="mi--tabla" value={aplicar[d.id] ?? ''} aria-label={`Pago a ${d.documento || 'factura'}`}
                  onChange={(v) => setAplicar({ ...aplicar, [d.id]: v })} /></td>
                <td data-label="Saldo nuevo" className="num">{toMoney(d.saldo - pago)}</td></tr>;
            })}</tbody>
          </table>) : <p className="fx-empty">Este proveedor no tiene facturas pendientes en la sucursal del cierre.</p>)}

        <div className="ct-total" style={{ marginTop: 12, background: excede ? 'var(--danger-bg)' : undefined }}>
          {excede ? 'El monto supera el efectivo pendiente de liquidar.' : 'Monto a aplicar'} <strong>{toMoney(monto)}</strong>
        </div>
        <div className="fx-actions"><button type="button" className="fx-btn" onClick={onClose}>Cancelar</button><button className="fx-btn fx-btn--primary" disabled={busy || excede}>{busy ? 'Guardando…' : 'Registrar'}</button></div>
      </form>
    </FondoModal>
  );
}
